/**
 * PresenceIndicator — colored dot + optional label showing online status.
 *
 *   <PresenceIndicator lastSeen={date} />              dot only
 *   <PresenceIndicator lastSeen={date} variant="full" />   dot + "Online" / "5m ago"
 *   <PresenceIndicator lastSeen={date} variant="verbose" /> dot + "Last seen 5m ago"
 *   <PresenceIndicator lastSeen={date} dotPlacement="overlay" />
 *
 * Drives styling from the lib/presence tone helper so the look stays
 * consistent everywhere (online = emerald, recent = amber, etc.).
 */
import { presenceTone, formatPresence, formatPresenceVerbose } from '../lib/presence';
import './PresenceIndicator.css';

interface Props {
    lastSeen: Date | null | undefined;
    /** Visual style:
     *   'dot'     — just the colored dot (default)
     *   'full'    — dot + "Online" / "5m ago"
     *   'verbose' — dot + "Last seen 5m ago"
     */
    variant?: 'dot' | 'full' | 'verbose';
    /** When true, render the dot positioned over the bottom-right of the parent
     *  (parent must be position:relative). Useful for avatar overlays. */
    dotPlacement?: 'inline' | 'overlay';
    /** Pixel size for the dot (default 8) */
    size?: number;
    /** Show "Offline" label when the user has never been seen (default false). */
    showOfflineLabel?: boolean;
}

export default function PresenceIndicator({
    lastSeen,
    variant = 'dot',
    dotPlacement = 'inline',
    size = 8,
    showOfflineLabel = false,
}: Props) {
    const tone = presenceTone(lastSeen);

    if (tone === 'unknown' && !showOfflineLabel) {
        return null;
    }

    const label = variant === 'verbose'
        ? formatPresenceVerbose(lastSeen)
        : formatPresence(lastSeen);

    const dotStyle: React.CSSProperties = { width: size, height: size };

    if (dotPlacement === 'overlay') {
        return (
            <span
                className={`presence-dot presence-dot--overlay presence-tone-${tone}`}
                style={dotStyle}
                aria-label={label}
                title={label}
            />
        );
    }

    if (variant === 'dot') {
        return (
            <span
                className={`presence-dot presence-tone-${tone}`}
                style={dotStyle}
                aria-label={label}
                title={label}
            />
        );
    }

    return (
        <span className={`presence-pill presence-tone-${tone}`} title={label}>
            <span className="presence-pill__dot" style={dotStyle} />
            <span className="presence-pill__label">{label}</span>
        </span>
    );
}
